import {
    SxProps,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyState } from '@/components';
import { useIsGstTaxRegion } from '@/hooks/useIsGstTaxRegion';
import { COLOR } from '@/theme/colors';

import InvoicesTableItem from './components/InvoicesTableItem';
import { Props } from './types';

const headerFontStyle: SxProps = {
    color: COLOR.paleSky,
    fontWeight: 700,
    fontSize: 13,
};

function InvoicesTable({
    emptyStateMessage,
    rows,
    selectable,
    selectItem,
    isItemChecked,
    navigationState,
}: Props) {
    const { t } = useTranslation();
    const { isGstTaxRegion } = useIsGstTaxRegion();

    return (
        <TableContainer sx={{ overflowX: 'clip' }}>
            {!rows.length && emptyStateMessage ? (
                <EmptyState
                    testID={'InvoicesTable-EmptyStateMessage'}
                    message={emptyStateMessage}
                />
            ) : null}
            {rows.length ? (
                <Table sx={{ minWidth: 650 }} aria-label="invoices table">
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography sx={headerFontStyle}>
                                    {t('InvoicesPage:columns:to')}
                                </Typography>
                            </TableCell>

                            <TableCell align="left">
                                <Typography
                                    variant="caption"
                                    sx={headerFontStyle}
                                >
                                    {`${t('InvoicesPage:columns:total')}${
                                        isGstTaxRegion ? '(exGST)' : ''
                                    }`}
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="caption"
                                    sx={headerFontStyle}
                                >
                                    {t('InvoicesPage:columns:overdue')}
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="caption"
                                    sx={headerFontStyle}
                                >
                                    {t('InvoicesPage:columns:status')}
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="caption"
                                    sx={headerFontStyle}
                                >
                                    {t('InvoicesPage:columns:created_at')}
                                </Typography>
                            </TableCell>
                            <TableCell align="left">
                                <Typography
                                    variant="caption"
                                    sx={headerFontStyle}
                                >
                                    {t('InvoicesPage:columns:updated_at')}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody data-test-id={'InvoicesPage-TableBody'}>
                        {rows.map((row) => (
                            <InvoicesTableItem
                                invoice={row}
                                key={row.id}
                                selectable={selectable}
                                isSelected={isItemChecked(row)}
                                onSelect={() => selectItem(row)}
                                navigationState={navigationState}
                            />
                        ))}
                    </TableBody>
                </Table>
            ) : null}
        </TableContainer>
    );
}

export default memo(InvoicesTable);
